import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

/**
 * Community Resource — posts of the "Community Resource" category of the Skool community,
 * mirrored daily by skool-classroom-importer (source kind "feed"). The list is public;
 * `skool_post_bodies` and `skool_post_attachments` are gated by RLS (members + admins), which is
 * why the body comes back null and the attachments empty for everyone else.
 */
export type CommunityPost = Tables<"skool_posts">;
export type CommunityAttachment = Tables<"skool_post_attachments">;

export const POST_LIST_COLUMNS =
  "id, slug, title, author_name, author_handle, summary, cover_url, skool_url, posted_at, posted_month, has_video, images_count, files_count, upvotes, comments_count, pinned, label_name, last_changed_at";

export type PostListRow = Pick<
  CommunityPost,
  | "id"
  | "slug"
  | "title"
  | "author_name"
  | "author_handle"
  | "summary"
  | "cover_url"
  | "skool_url"
  | "posted_at"
  | "posted_month"
  | "has_video"
  | "images_count"
  | "files_count"
  | "upvotes"
  | "comments_count"
  | "pinned"
  | "label_name"
  | "last_changed_at"
>;

/** Every active post, newest first (a few hundred rows at most; grouped client-side). */
export const useCommunityPosts = () =>
  useQuery({
    queryKey: ["community-posts", "index"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("skool_posts")
        .select(POST_LIST_COLUMNS)
        .eq("status", "active")
        .order("posted_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as PostListRow[];
    },
    staleTime: 5 * 60_000,
  });

/** One post, its attachments and (when RLS allows) its body. */
export const useCommunityPost = (slug: string | undefined) =>
  useQuery({
    queryKey: ["community-posts", "post", slug],
    enabled: !!slug,
    queryFn: async () => {
      const { data: post, error } = await supabase.from("skool_posts").select("*").eq("slug", slug!).maybeSingle();
      if (error) throw error;
      if (!post) return null;
      const [{ data: attachments, error: aErr }, { data: body }] = await Promise.all([
        // Empty for non-members: RLS filters the rows rather than erroring.
        supabase.from("skool_post_attachments").select("*").eq("post_id", post.id).order("sort_order", { ascending: true }),
        supabase.from("skool_post_bodies").select("body_md").eq("post_id", post.id).maybeSingle(),
      ]);
      if (aErr) throw aErr;
      return {
        post: post as CommunityPost,
        attachments: (attachments ?? []) as CommunityAttachment[],
        bodyMd: body?.body_md ?? null,
      };
    },
  });

/** Short-lived URL for a mirrored file (bucket skool-files); only mints when the storage policy passes. */
export const signedCommunityFileUrl = async (storagePath: string, seconds = 120): Promise<string> => {
  const { data, error } = await supabase.storage.from("skool-files").createSignedUrl(storagePath, seconds);
  if (error || !data?.signedUrl) throw error ?? new Error("Could not sign the file URL");
  return data.signedUrl;
};

/** "2026-08" → "August 2026". Month keys are UTC on purpose (what the importer stores). */
export const monthLabel = (key: string): string => {
  const [y, m] = key.split("-").map(Number);
  if (!y || !m) return key;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
};

export const formatPostDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export type MonthGroup = { key: string; label: string; items: PostListRow[] };

/** Group posts by `posted_month`, newest month first; rows keep their newest-first order. */
export const groupByMonth = (posts: PostListRow[]): MonthGroup[] => {
  const map = new Map<string, PostListRow[]>();
  for (const p of posts) {
    if (!map.has(p.posted_month)) map.set(p.posted_month, []);
    map.get(p.posted_month)!.push(p);
  }
  return [...map.entries()]
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .map(([key, items]) => ({ key, label: monthLabel(key), items }));
};
